import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Shield, Lock, Server, Eye, Key, FileCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Security: React.FC = () => {
  const { t } = useTranslation();

  const securityFeatures = [
    {
      icon: Lock,
      title: t('legal.security.features.encryption.title'),
      description: t('legal.security.features.encryption.description'),
    },
    {
      icon: Server,
      title: t('legal.security.features.infrastructure.title'),
      description: t('legal.security.features.infrastructure.description'),
    },
    {
      icon: Eye,
      title: t('legal.security.features.privacy.title'),
      description: t('legal.security.features.privacy.description'),
    },
    {
      icon: Key,
      title: t('legal.security.features.access.title'),
      description: t('legal.security.features.access.description'),
    },
    {
      icon: FileCheck,
      title: t('legal.security.features.processing.title'),
      description: t('legal.security.features.processing.description'),
    },
    {
      icon: Shield,
      title: t('legal.security.features.audits.title'),
      description: t('legal.security.features.audits.description'),
    },
  ];

  const dataProtectionItems = t('legal.security.sections.dataProtection.items', { returnObjects: true }) as string[];
  const infrastructureItems = t('legal.security.sections.infrastructureSecurity.items', { returnObjects: true }) as string[];
  const accountItems = t('legal.security.sections.accountSecurity.items', { returnObjects: true }) as string[];
  const aiItems = t('legal.security.sections.aiMachineLearning.items', { returnObjects: true }) as string[];
  const complianceItems = t('legal.security.sections.compliance.items', { returnObjects: true }) as string[];

  return (
    <>
      <Helmet>
        <title>{t('legal.security.title')} - OmniScribe</title>
        <meta name="description" content={t('legal.security.heroSubtitle')} />
      </Helmet>

      <div className="min-h-screen bg-white dark:bg-zinc-950 py-16 transition-colors duration-300">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center h-16 w-16 bg-indigo-100 dark:bg-indigo-500/10 rounded-2xl mb-6">
              <Shield className="h-8 w-8 text-indigo-600 dark:text-indigo-400" />
            </div>
            <h1 className="h-hero mb-4">{t('legal.security.heroTitle')}</h1>
            <p className="text-lg text-slate-600 dark:text-zinc-400 max-w-2xl mx-auto">
              {t('legal.security.heroSubtitle')}
            </p>
          </div>

          {/* Security Features Grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-16">
            {securityFeatures.map((feature, index) => (
              <div
                key={index}
                className="p-6 bg-slate-50 dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800"
              >
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 bg-indigo-100 dark:bg-indigo-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <feature.icon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">{feature.title}</h3>
                    <p className="text-slate-600 dark:text-zinc-400 text-sm leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Detailed Sections */}
          <div className="prose prose-slate dark:prose-invert max-w-none">
            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">{t('legal.security.sections.dataProtection.title')}</h2>
              <p className="text-slate-600 dark:text-zinc-400 leading-relaxed mb-4">
                {t('legal.security.sections.dataProtection.intro')}
              </p>
              <ul className="list-disc list-inside text-slate-600 dark:text-zinc-400 space-y-2 ml-4">
                {dataProtectionItems.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">{t('legal.security.sections.infrastructureSecurity.title')}</h2>
              <p className="text-slate-600 dark:text-zinc-400 leading-relaxed mb-4">
                {t('legal.security.sections.infrastructureSecurity.intro')}
              </p>
              <ul className="list-disc list-inside text-slate-600 dark:text-zinc-400 space-y-2 ml-4">
                {infrastructureItems.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">{t('legal.security.sections.accountSecurity.title')}</h2>
              <p className="text-slate-600 dark:text-zinc-400 leading-relaxed mb-4">
                {t('legal.security.sections.accountSecurity.intro')}
              </p>
              <ul className="list-disc list-inside text-slate-600 dark:text-zinc-400 space-y-2 ml-4">
                {accountItems.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">{t('legal.security.sections.aiMachineLearning.title')}</h2>
              <p className="text-slate-600 dark:text-zinc-400 leading-relaxed mb-4">
                {t('legal.security.sections.aiMachineLearning.intro')}
              </p>
              <ul className="list-disc list-inside text-slate-600 dark:text-zinc-400 space-y-2 ml-4">
                {aiItems.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">{t('legal.security.sections.compliance.title')}</h2>
              <p className="text-slate-600 dark:text-zinc-400 leading-relaxed mb-4">
                {t('legal.security.sections.compliance.intro')}
              </p>
              <ul className="list-disc list-inside text-slate-600 dark:text-zinc-400 space-y-2 ml-4">
                {complianceItems.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">{t('legal.security.sections.vulnerability.title')}</h2>
              <p className="text-slate-600 dark:text-zinc-400 leading-relaxed mb-4">
                {t('legal.security.sections.vulnerability.intro')}
              </p>
              <div className="p-4 bg-slate-50 dark:bg-zinc-900 rounded-lg border border-slate-200 dark:border-zinc-800">
                <p className="text-slate-700 dark:text-zinc-300 font-medium mb-2">{t('legal.security.sections.vulnerability.contactTitle')}</p>
                <p className="text-slate-600 dark:text-zinc-400">{t('legal.security.sections.vulnerability.contactEmail')}</p>
                <p className="text-slate-600 dark:text-zinc-400 mt-2 text-sm">
                  {t('legal.security.sections.vulnerability.contactDescription')}
                </p>
              </div>
            </section>

            <section className="mb-10">
              <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">{t('legal.security.sections.questions.title')}</h2>
              <p className="text-slate-600 dark:text-zinc-400 leading-relaxed">
                {t('legal.security.sections.questions.intro')}
              </p>
              <div className="mt-4 p-4 bg-slate-50 dark:bg-zinc-900 rounded-lg border border-slate-200 dark:border-zinc-800">
                <p className="text-slate-700 dark:text-zinc-300 font-medium">{t('legal.security.sections.questions.company')}</p>
                <p className="text-slate-600 dark:text-zinc-400">{t('legal.security.sections.questions.contactEmail')}</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default Security;
